/**
 * /me/projects — the signed-in person's projects as the server keeps them (O5, owner decision D3).
 *
 * Only the wire: what to send and what comes back. Caching, the version a write is made from,
 * and what a refusal means to the person live in platform/projectStore.server.ts.
 */
import type { AppDefinition } from '@/types/app';
import type { ChatMessage } from '@/llm/types';
import { ApiError, request } from './client';

export interface ServerProjectSummary {
  id: string;
  name: string;
  updatedAt: string;
  version: number;
  /**
   * What a list row shows, copied from the definition at each save, so a list can be drawn
   * without every body. ABSENT on a row saved before the server kept it: the body is fetched.
   */
  listing?: ProjectListing;
}

export interface ProjectListing {
  runtime?: string;
  hostedAppId?: string;
  webUrl?: string;
  unfinished: boolean;
}

export interface ServerProject {
  id: string;
  name: string;
  description: string;
  definition: AppDefinition;
  messages: ChatMessage[];
  createdAt?: string;
  updatedAt: string;
  version: number;
}

/** The server's answer when the caller already holds the version it has. */
export interface NotModified {
  id: string;
  version: number;
  notModified: true;
}

export interface ProjectBody {
  name: string;
  description: string;
  definition: AppDefinition;
  messages: ChatMessage[];
}

/**
 * A 2 MB body takes a while on a phone's upload; the gateway allows 29 seconds and an ordinary
 * call's 15 would time out a save that is still arriving — and a timed-out save is one whose
 * outcome nobody knows.
 */
const SAVE_TIMEOUT_MS = 29_000;
const READ_TIMEOUT_MS = 20_000;

const path = (id: string) => `/me/projects/${encodeURIComponent(id)}`;

export interface ProjectsApi {
  list(): Promise<ServerProjectSummary[]>;
  /** Null when there is no such project of this person's. */
  get(id: string, known?: number): Promise<ServerProject | NotModified | null>;
  /** Rejects with an ApiError of kind `conflict` (reason `changed_meanwhile`) when `baseVersion` is stale. */
  put(id: string, body: ProjectBody, baseVersion: number): Promise<{ version: number; updatedAt: string }>;
  remove(id: string): Promise<void>;
}

export const projectsApi: ProjectsApi = {
  async list() {
    const res = await request<{ projects?: ServerProjectSummary[] }>('/me/projects', { timeoutMs: READ_TIMEOUT_MS });
    return res?.projects ?? [];
  },

  async get(id, known) {
    try {
      return await request<ServerProject | NotModified>(
        known === undefined ? path(id) : `${path(id)}?known=${encodeURIComponent(String(known))}`,
        { timeoutMs: READ_TIMEOUT_MS },
      );
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'not_found') return null;
      throw e;
    }
  },

  async put(id, body, baseVersion) {
    return request<{ version: number; updatedAt: string }>(path(id), {
      method: 'PUT',
      body: { ...body, baseVersion },
      timeoutMs: SAVE_TIMEOUT_MS,
    });
  },

  async remove(id) {
    await request<unknown>(path(id), { method: 'DELETE' });
  },
};
